import type { AudioEngine } from './audioEngine.js';

const CROSSFADE_SEC = 2;
// VO ducking: music pulls to ~-8dB, ambient bed rides a parallel, shallower duck.
// Ramps are asymmetric — a quick attack, a slow (~1.2s) release so the bed swells
// back gently after a line rather than snapping.
const DUCK_ATTACK_SEC = 0.35;
const DUCK_RELEASE_SEC = 1.2;
const DUCKED_MUSIC = 0.4; // ~-8 dB
const DUCKED_AMBIENCE = 0.5; // ~-6 dB parallel duck under VO
const FULL_GAIN = 1;
// Combat-exit hysteresis: hold tension music this long after the last hostile is
// gone before dropping to flight, so a single kill doesn't whiplash the score.
const COMBAT_EXIT_HOLD_SEC = 7;

export type MusicContext = 'menu' | 'docked' | 'flight' | 'tension' | 'cenotaph';
export type MusicTrackId =
  | 'music-menu'
  | 'music-docked'
  | 'music-flight'
  | 'music-tension'
  | 'music-cenotaph';
/** One-shot stings routed through the music output (ducked with music under VO). */
export type MusicStingId = 'music-aftermath-sting';

export interface MusicContextSnapshot {
  menuOpen: boolean;
  docked: boolean;
  nearCenotaph: boolean;
  verifying: boolean;
  combatActive: boolean;
}

export interface MusicPlaybackState {
  context: MusicContext;
  ducked: boolean;
}

interface RuntimeTrack {
  gain: GainNode | null;
  buffer: AudioBuffer | null;
  source: AudioBufferSourceNode | null;
  status: 'idle' | 'loading' | 'ready' | 'missing';
}

const TRACKS: readonly MusicTrackId[] = [
  'music-menu',
  'music-docked',
  'music-flight',
  'music-tension',
  'music-cenotaph',
];

const TRACK_BY_CONTEXT: Record<MusicContext, MusicTrackId> = {
  menu: 'music-menu',
  docked: 'music-docked',
  flight: 'music-flight',
  tension: 'music-tension',
  cenotaph: 'music-cenotaph',
};

/** Aftermath sting gain relative to the music output — sits just under the bed. */
const STING_GAIN = 0.85;

const createRuntimeTrack = (): RuntimeTrack => ({
  gain: null,
  buffer: null,
  source: null,
  status: 'idle',
});

const clonePlaybackState = (state: MusicPlaybackState): MusicPlaybackState => ({
  context: state.context,
  ducked: state.ducked,
});

const samePlaybackState = (
  a: MusicPlaybackState | null,
  b: MusicPlaybackState,
): boolean => !!a && a.context === b.context && a.ducked === b.ducked;

const trackUrls = (id: MusicTrackId | MusicStingId): readonly string[] => [
  `/assets/audio/${id}.mp3`,
  `/assets/audio/${id}.ogg`,
];

const rampGain = (
  ctx: AudioContext,
  param: AudioParam,
  target: number,
  duration: number,
): void => {
  const now = ctx.currentTime;
  param.cancelScheduledValues(now);
  param.setValueAtTime(param.value, now);
  param.linearRampToValueAtTime(target, now + duration);
};

const createLoopSource = (
  ctx: AudioContext,
  buffer: AudioBuffer,
  gain: GainNode,
): AudioBufferSourceNode => {
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  source.connect(gain);
  source.start();
  return source;
};

const decodeTrack = async (
  ctx: AudioContext,
  url: string,
): Promise<AudioBuffer | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    return await ctx.decodeAudioData(await response.arrayBuffer());
  } catch {
    return null;
  }
};

export const loadTrackBuffer = async (
  ctx: AudioContext,
  id: MusicTrackId | MusicStingId,
): Promise<AudioBuffer | null> => {
  for (const url of trackUrls(id)) {
    const buffer = await decodeTrack(ctx, url);
    if (buffer) return buffer;
  }
  return null;
};

/**
 * Context priority (highest first): menu/pause > docked > cenotaph/verify > tension >
 * flight. `docked` sits above the cenotaph and combat beds so a station berth always
 * reads as its own calm bed, but the pause/menu snapshot still preempts it (the menu
 * bed owns the bus while paused). Docking away from combat is the norm; `docked`
 * intentionally outranks `tension` since the FSM zeroes throttle and frees the cursor.
 */
export const selectMusicContext = (snapshot: MusicContextSnapshot): MusicContext => {
  if (snapshot.menuOpen) return 'menu';
  if (snapshot.docked) return 'docked';
  if (snapshot.nearCenotaph || snapshot.verifying) return 'cenotaph';
  if (snapshot.combatActive) return 'tension';
  return 'flight';
};

export const trackForMusicContext = (context: MusicContext): MusicTrackId =>
  TRACK_BY_CONTEXT[context];

export interface HysteresisResult {
  context: MusicContext;
  holdUntil: number;
}

/**
 * Combat-exit hysteresis (pure). Given the previously-applied context, the newly
 * requested one, the current time, the active hold deadline, and the hold length:
 *  - a `tension` request (re)arms the hold and passes through;
 *  - a `flight` request while still holding after tension keeps `tension`;
 *  - anything else (menu / cenotaph, or the hold has expired) passes through.
 * A single kill therefore can't snap tension→flight before the hold elapses, while
 * pause/cenotaph still preempt immediately.
 */
export const resolveCombatHysteresis = (
  prevContext: MusicContext,
  nextContext: MusicContext,
  now: number,
  holdUntil: number,
  holdSec: number,
): HysteresisResult => {
  if (nextContext === 'tension') return { context: 'tension', holdUntil: now + holdSec };
  if (nextContext === 'flight' && prevContext === 'tension' && now < holdUntil) {
    return { context: 'tension', holdUntil };
  }
  return { context: nextContext, holdUntil };
};

export class MusicManager {
  private readonly tracks: Record<MusicTrackId, RuntimeTrack> = {
    'music-menu': createRuntimeTrack(),
    'music-docked': createRuntimeTrack(),
    'music-flight': createRuntimeTrack(),
    'music-tension': createRuntimeTrack(),
    'music-cenotaph': createRuntimeTrack(),
  };
  private output: GainNode | null = null;
  private applied: MusicPlaybackState | null = null;
  private state: MusicPlaybackState = { context: 'menu', ducked: false };
  private tensionHoldUntil = 0;
  // Aftermath sting: lazily-decoded one-shot fired once per encounter-cleared.
  private stingBuffer: AudioBuffer | null = null;
  private stingStatus: 'idle' | 'loading' | 'ready' | 'missing' = 'idle';

  constructor(private readonly engine: AudioEngine) {}

  resume(): void {
    this.engine.resume();
    if (!this.ensureGraph()) return;
    this.applyState();
  }

  setState(state: MusicPlaybackState): void {
    const resolved = this.applyCombatHysteresis(state);
    this.state = clonePlaybackState(resolved);
    if (!this.ensureGraph()) {
      this.applied = null;
      return;
    }
    if (!samePlaybackState(this.applied, resolved)) this.applyState();
  }

  /**
   * ENCOUNTER_CLEARED aftermath sting: a single one-shot routed through the music
   * OUTPUT (not a fresh bus), so it ducks together with the bed whenever VO is active
   * and folds under the master/music faders. Fire-and-forget; the caller owns the
   * once-per-encounter latch. A missing asset degrades to silence like every other
   * cue. No-op until the context is resumed and the buffer has decoded.
   */
  playAftermathSting(): void {
    const ctx = this.engine.audioContext;
    if (!ctx || !this.ensureGraph() || !this.output) return;
    this.beginStingLoad(ctx);
    if (!this.stingBuffer) return;
    const gain = ctx.createGain();
    gain.gain.value = STING_GAIN;
    gain.connect(this.output);
    const source = ctx.createBufferSource();
    source.buffer = this.stingBuffer;
    source.connect(gain);
    source.onended = (): void => {
      source.disconnect();
      gain.disconnect();
    };
    source.start();
  }

  /** Lazily decode the aftermath sting once; preloaded at graph init so it is ready
   * by the time the first encounter clears (which is minutes into a session). */
  private beginStingLoad(ctx: AudioContext): void {
    if (this.stingStatus !== 'idle') return;
    this.stingStatus = 'loading';
    void loadTrackBuffer(ctx, 'music-aftermath-sting').then((buffer) => {
      this.stingBuffer = buffer;
      this.stingStatus = buffer ? 'ready' : 'missing';
    });
  }

  /** Hold tension music for a beat after combat clears (see resolveCombatHysteresis).
   * Uses the AudioContext clock; a no-context frame passes the request through. */
  private applyCombatHysteresis(next: MusicPlaybackState): MusicPlaybackState {
    const ctx = this.engine.audioContext;
    if (!ctx) return next;
    const result = resolveCombatHysteresis(
      this.state.context,
      next.context,
      ctx.currentTime,
      this.tensionHoldUntil,
      COMBAT_EXIT_HOLD_SEC,
    );
    this.tensionHoldUntil = result.holdUntil;
    return { context: result.context, ducked: next.ducked };
  }

  private ensureGraph(): boolean {
    const ctx = this.engine.audioContext;
    const bus = this.engine.musicBus;
    if (!ctx || !bus) return false;
    if (this.output) return true;
    this.output = ctx.createGain();
    this.output.gain.value = FULL_GAIN;
    this.output.connect(bus);
    this.initTrackNodes(ctx);
    this.beginStingLoad(ctx); // preload the aftermath sting so it's ready on first clear
    return true;
  }

  private initTrackNodes(ctx: AudioContext): void {
    for (const id of TRACKS) {
      const track = this.tracks[id];
      track.gain = ctx.createGain();
      track.gain.gain.value = 0;
      track.gain.connect(this.output!);
      this.beginTrackLoad(id, ctx);
    }
  }

  private beginTrackLoad(id: MusicTrackId, ctx: AudioContext): void {
    const track = this.tracks[id];
    if (track.status !== 'idle') return;
    track.status = 'loading';
    void this.finishTrackLoad(id, ctx);
  }

  private async finishTrackLoad(id: MusicTrackId, ctx: AudioContext): Promise<void> {
    const track = this.tracks[id];
    track.buffer = await loadTrackBuffer(ctx, id);
    track.status = track.buffer ? 'ready' : 'missing';
    if (!track.buffer || !track.gain || track.source) return;
    track.source = createLoopSource(ctx, track.buffer, track.gain);
    this.applyState();
  }

  private applyState(): void {
    const ctx = this.engine.audioContext;
    if (!ctx || !this.output) return;
    this.rampDuck(ctx, this.output.gain, this.state.ducked ? DUCKED_MUSIC : FULL_GAIN);
    const amb = this.engine.ambienceDuck;
    if (amb) this.rampDuck(ctx, amb.gain, this.state.ducked ? DUCKED_AMBIENCE : FULL_GAIN);
    const active = trackForMusicContext(this.state.context);
    for (const id of TRACKS) {
      const gain = this.tracks[id].gain?.gain;
      if (!gain) continue;
      rampGain(ctx, gain, id === active && this.tracks[id].source ? 1 : 0, CROSSFADE_SEC);
    }
    this.applied = clonePlaybackState(this.state);
  }

  /** Ramp a duck param with a quick attack (ducking down) and a slow release
   * (~1.2s, swelling back up) so lines duck crisply but recover gently. */
  private rampDuck(ctx: AudioContext, param: AudioParam, target: number): void {
    const dur = target < param.value ? DUCK_ATTACK_SEC : DUCK_RELEASE_SEC;
    rampGain(ctx, param, target, dur);
  }
}
